import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  RadioGroup,
  Zoom,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import SuccessPopup from "./SuccessPopup";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import * as Yup from "yup";
import { ticketInitialValue, ticketValidation } from "../../../view/Landing/CustomerBookTicket/TicketModel";
import {
  exhibitions,
  guides,
  settingApi,
  ticketsAction,
  ticketsApi,
  ticketsType,
  visitorTypes,
  getTourGuideByCid,
  getTourOperatorByLicense,
} from "../../../services/TicketsServices";
import { SNACKBAR_OPEN } from "../../../reducers";
import { DispatchContext } from "../../../store";
import countryList from "react-select-country-list";

const addVisitorValidation = ticketValidation.shape({
  visitor_type_id: Yup.mixed().nullable().notRequired(),
  ticket_type_id: Yup.mixed().nullable().notRequired(),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom style={{ transitionDelay: "150ms" }} ref={ref} {...props} />;
});

export default function AddVisitor(props) {
  const dispatch = useContext(DispatchContext);
  const user = JSON.parse(localStorage.getItem("user"));
  const museumId = user?.museums?.[0]?.id;
  const { open, handleAction, visitorId, gettingTicketList } = props;
  const [show, setShow] = useState(false);
  const [initialValues, setInitialValues] = useState({
    ...ticketInitialValue,
    guide_id: null,
    visitor_type_id: null,
    museum_id: museumId,
    validity: new Date().toISOString().split('T')[0],
    payment_type: "cash"
  });
  const options = useMemo(() => {
    const data = countryList().getData();
    return [...data, { value: "others", label: "Others" }];
  }, []);
  const [ticketId, setTicketId] = useState({});
  const [settings, setSettings] = useState([]);
  const [withGuide, setWithGuide] = useState("yes");
  const isAdmin = user?.role === "admin";
  const [guideOptions, setGuideOptions] = useState([]);
  const [ticketTypeOptions, setTicketTypeOptions] = useState([]);
  const [visitorTypeOptions, setVisitorTypeOptions] = useState([]);
  const [tourOperatorOptions, setTourOperatorOptions] = useState([]);
  const [guideCid, setGuideCid] = useState('');
  const [tourOperatorLicense, setTourOperatorLicense] = useState('');
  const [ttNextPage, setTtNextPage] = useState(1);
  const [ttHasNext, setTtHasNext] = useState(true);
  const [ttLoading, setTtLoading] = useState(false);

  const calculateDisplayAmount = (noOfVisitors, rate) => {
    const visitors = Number(noOfVisitors || 0);
    const visitorRate = Number(rate || 0);
    return visitors * visitorRate;
  };

  const getCurrentMuseumTypeId = () => {
    return user?.museums?.[0]?.museum_type?.id || user?.museum_type_id || null;
  };

  const handleGuideCidLookup = async (cid, setFieldValue) => {
    if (!cid || cid.length < 10) return;

    try {
      const response = await getTourGuideByCid(cid);
      if (response?.data?.users?.user?.length > 0) {
        const guide = response.data.users.user[0];
        setFieldValue('guide_name', guide.name || '');
        setFieldValue('guide_phone', guide.mobileNo || '');
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: 'success', message: 'Guide information found!' }
        });
      }
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'error', message: 'Guide not found with this CID' }
      });
    }
  };

  const handleTourOperatorSearch = async (licenseNo, setFieldValue) => {
    if (!licenseNo) return;

    try {
      const response = await getTourOperatorByLicense(licenseNo);
      if (response?.data) {
        const operator = response.data;
        setFieldValue('guide_agent_name', operator.pro_Estb_Name || '');
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: 'success', message: 'Tour operator found!' }
        });
      }
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'error', message: 'Tour operator not found with this license' }
      });
    }
  };

  const filterTicketTypesByMuseum = (arr) => {
    const list = arr || [];
    if (list.some((t) => t?.museum_id)) {
      return list.filter((t) => String(t?.museum_id) === String(museumId));
    }
    const currentMuseumTypeId = getCurrentMuseumTypeId();
    if (currentMuseumTypeId && list.some((t) => t?.museum_type?.id)) {
      return list.filter((t) => String(t?.museum_type?.id) === String(currentMuseumTypeId));
    }
    return list;
  };

  const fetchTicketTypes = async (page = 1) => {
    if (!museumId || ttLoading) return;
    setTtLoading(true);
    try {
      const res = await ticketsType("get", null, { page, per_page: 20, status: "active", museum_id: museumId });
      const list = filterTicketTypesByMuseum(res?.data || []);
      setTicketTypeOptions((prev) => {
        const map = new Map(prev.map((x) => [String(x.id), x]));
        list.forEach((x) => map.set(String(x.id), x));
        return Array.from(map.values());
      });
      const next = res?.meta?.next_page;
      setTtNextPage(next || null);
      setTtHasNext(!!next);
    } catch (err) {
      console.error("Failed to load ticket types:", err);
      if (page === 1) setTicketTypeOptions([]);
      setTtHasNext(false);
    } finally {
      setTtLoading(false);
    }
  };

  const fetchVisitorTypes = async () => {
    try {
      const res = await visitorTypes("get", null, { status: "active" });
      setVisitorTypeOptions(res?.data || []);
    } catch (err) {
      console.error("Failed to load visitor types:", err);
      setVisitorTypeOptions([]);
    }
  };


  useEffect(() => {
    setTicketTypeOptions([]); setTtNextPage(1); setTtHasNext(true);
  }, [museumId]);

  useEffect(() => {
    fetchVisitorTypes();
  }, []);

  useEffect(() => {
    if (!museumId) {
      setGuideOptions([]);
      return;
    }

    guides("get", null, { page: 1, per_page: 200, museum_id: museumId, status: "active" })
      .then((res) => {
        setGuideOptions(res?.data || []);
      })
      .catch((err) => {
        console.error("Failed to load guides:", err);
        setGuideOptions([]);
      });
  }, [museumId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (open && visitorId) {
      ticketsAction("get", visitorId).then((res) => {
        const ticket = res?.data || {};

        setWithGuide(ticket?.guide_id ? "yes" : "no");
        const finalInitialValues = {
          museum_id: museumId,
          validity: formatDate(ticket?.validity),
          guide_id: ticket?.guide_id || null,
          visitor_type_id: ticket?.visitor_type_id || ticket?.ticket_type_detail?.visitor_type?.id || null,
          withGuide: ticket?.guide_id ? "yes" : "no",
          nationality: ticket?.nationality || "",
          nationality_other: ticket?.nationality_other || "",
          no_of_visitor: ticket?.no_of_visitor || null,
          payment_due: ticket?.payment_due || "",
          payment_type: ticket?.payment_type || "cash",
          transaction_no: ticket?.transaction_no || "",
          guide_name: ticket?.guide_name || "",
          guide_phone: ticket?.guide_phone || "",
          guide_agent_name: ticket?.guide_agent_name || "",
        };
        setInitialValues(finalInitialValues);
      });
    }
  }, [open, visitorId]);

  useEffect(() => {
    if (open && visitorId) {
      if (ticketTypeOptions.length === 0) fetchTicketTypes(1);
    }
  }, [open, visitorId]);

  const handleGuideSelection = (event) => {
    setWithGuide(event.target.value);
  };

  const onSubmit = async (values) => {

    const visitorTypeId = Number(values?.visitor_type_id);
    const ticketValues = {
      museum_id: museumId,
      visitor_type_id: visitorTypeId,
      nationality: values?.nationality || "",
      nationality_other: values?.nationality_other || "",
      no_of_visitor: parseInt(values.no_of_visitor),
      payment_due: Number(values.payment_due) || 0,
      payment_type: values.payment_type || "cash",
      translations: {
        en: {
          remarks: "",
          refund_notes: "",
        },
      },
    };

    // Always include guide fields regardless of guide selection
    ticketValues.guide_name = values.guide_name || "";
    ticketValues.guide_phone = values.guide_phone || "";
    ticketValues.guide_agent_name = values.guide_agent_name || "";

    // Only include guide_id if with guide and a guide is selected
    if (withGuide === "yes") {
      const guideId = Number(values?.guide_id);
      if (Number.isFinite(guideId) && guideId > 0) {
        ticketValues.guide_id = guideId;
      }
    }

    if (values.payment_type !== "cash" && values.transaction_no) {
      ticketValues.transaction_no = values.transaction_no;
    }


    if (visitorId) {
      ticketsAction("put", visitorId, { ...ticketValues }).then((res) => {
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: "success", message: "Ticket updated successfully" },
        });
        handleAction();
        gettingTicketList(true);
      }).catch((error) => {
        console.error('Update ticket error:', error);
      });
    } else {
      await ticketsApi("post", { ...ticketValues }).then((res) => {
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: "success", message: "Ticket created successfully" },
        });
        setTicketId(res?.data?.id);
        setShow(true);
        gettingTicketList(true);
      }).catch((error) => {
        console.error('Create ticket error:', error);
      });
    }
  };

  const handleClose = () => {
    setShow(false);
    handleAction();
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      aria-describedby="alert-dialog-slide-description"
      fullWidth={true}
      maxWidth={"sm"}
    >
      <DialogTitle color={"#616B88"} sx={{ fontSize: 30 }} className={"text-bold"} fontWeight={700}>
        {visitorId ? "Update Visitor" : "Book Ticket"}
      </DialogTitle>
      <Box className={"common-divider"} />
      <Formik
        initialValues={initialValues}
        onSubmit={(values, actions) => {
          return onSubmit(values);
        }}
        validationSchema={addVisitorValidation}
        validateOnBlur={true}
        enableReinitialize
      >
        {({ isSubmitting, touched, errors, values, setFieldValue, isValid, dirty, ...formik }) => {

          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item sm={6}>
                    <Autocomplete
                      id="visitor_type"
                      options={visitorTypeOptions}
                      value={visitorTypeOptions.find((option) => String(option.id) === String(values.visitor_type_id)) || null}
                      getOptionLabel={(option) => option?.name || ""}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option?.name || ""}
                        </li>
                      )}
                      size={"small"}
                      onChange={(e, value) => {
                        setFieldValue("visitor_type_id", value?.id || null);
                        setFieldValue("visitor_type", value?.name || "");
                      }}
                      renderInput={(params) => (
                        <Field
                          component={TextField}
                          {...params}
                          label="Select visitor type"
                          InputLabelProps={{
                            style: { color: "#929191", fontSize: 14 },
                          }}
                          name="visitor_type"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <Field
                      component={TextField}
                      variant={"outlined"}
                      size={"small"}
                      fullWidth
                      label={"No of visitor"}
                      name="no_of_visitor"
                      type={"number"}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue("no_of_visitor", value);
                        // Get fee directly from selected visitor type
                        const selectedVisitorType = visitorTypeOptions.find((vt) => String(vt?.id) === String(values?.visitor_type_id));
                        const matchedFee = selectedVisitorType?.fee;
                        const feeNum = Number(matchedFee);
                        if (value && Number.isFinite(feeNum) && feeNum > 0) {
                          const displayAmount = calculateDisplayAmount(value, feeNum);
                          setFieldValue("payment_due", displayAmount);
                        } else {
                          setFieldValue("payment_due", 0);
                        }
                      }}
                      InputLabelProps={{
                        style: { fontSize: 14, color: "#929191" },
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <Field
                      component={TextField}
                      variant={"outlined"}
                      size={"small"}
                      fullWidth
                      label={"Current Date"}
                      name="validity"
                      type={"date"}
                      InputLabelProps={{
                        style: { fontSize: 14, color: "#929191" },
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <Autocomplete
                      id="nationality"
                      options={options}
                      value={
                        (() => {
                          const found = options.find((option) => option.value === values.nationality);
                          return found || null;
                        })()
                      }
                      getOptionLabel={(option) => option?.label}
                      renderOption={(props, option) => (
                        <li {...props} key={option.value}>
                          {option?.label}
                        </li>
                      )}
                      size={"small"}
                      onChange={(e, value) => {
                        setFieldValue("nationality", value?.value || null);
                      }}
                      renderInput={(params) => (
                        <Field
                          component={TextField}
                          {...params}
                          label="Select country"
                          InputLabelProps={{
                            style: { color: "#929191", fontSize: 14 },
                          }}
                          name="nationality"
                        />
                      )}
                    />
                  </Grid>
                  {values?.nationality === "others" && (
                    <Grid item sm={6}>
                      <Field
                        component={TextField}
                        variant={"outlined"}
                        size={"small"}
                        fullWidth
                        label={"Specify nationality"}
                        name="nationality_other"
                        InputLabelProps={{
                          style: { fontSize: 14, color: "#929191" },
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item sm={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Select Payment Mode</FormLabel>
                      <RadioGroup
                        row
                        aria-label="payment_type"
                        name="payment_type"
                        value={values.payment_type}
                        onChange={(e) => setFieldValue("payment_type", e.target.value)}
                      >
                        <FormControlLabel
                          value="cash"
                          control={<Radio />}
                          label="Cash"
                          inputprops={{ "aria-label": "Cash" }}
                        />
                        <FormControlLabel
                          value="electronic"
                          control={<Radio />}
                          label="Electronic"
                          inputprops={{ "aria-label": "Electronic" }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item sm={6}>
                    <Field
                      component={TextField}
                      variant={"outlined"}
                      size={"small"}
                      fullWidth
                      label={"Payment due"}
                      name="payment_due"
                      type={"number"}
                      InputLabelProps={{
                        style: { fontSize: 14, color: "#929191" },
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">Nu.</InputAdornment>,
                      }}
                    />
                  </Grid>
                  {values?.payment_type === "electronic" && (
                    <Grid item sm={6}>
                      <Field
                        component={TextField}
                        variant={"outlined"}
                        size={"small"}
                        fullWidth
                        label={"Enter transaction number"}
                        name="transaction_no"
                        InputLabelProps={{
                          style: { fontSize: 14, color: "#929191" },
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
                <br />
                <RadioGroup row value={withGuide} onChange={handleGuideSelection}>
                  <FormControlLabel value="yes" control={<Radio />} label="With Guide" />
                  <FormControlLabel value="no" control={<Radio />} label="Without Guide" />
                </RadioGroup>
                <br />
                <Grid container spacing={2}>
                  {withGuide === "yes" && (
                    <Grid item sm={6}>
                      <Field
                        component={TextField}
                        variant={"outlined"}
                        size={"small"}
                        fullWidth
                        label={"Guide CID"}
                        name="guide_cid"
                        value={guideCid}
                        onChange={(e) => {
                          const value = e.target.value;
                          setGuideCid(value);

                          // Automatically lookup when 11 digits are entered
                          if (value.length === 11) {
                            console.log('AddVisitor: Auto-triggering CID lookup for 11 digits:', value);
                            handleGuideCidLookup(value, setFieldValue);
                          }
                        }}
                        InputLabelProps={{
                          style: { fontSize: 14, color: "#929191" },
                        }}
                      />
                    </Grid>
                  )}
                  {withGuide === "yes" ? (
                    <>
                      <Grid item sm={6}>
                        <Field
                          component={TextField}
                          variant={"outlined"}
                          size={"small"}
                          fullWidth
                          label="Guide Name"
                          name="guide_name"
                          InputLabelProps={{
                            style: { fontSize: 14, color: "#929191" },
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item sm={6}>
                        <Field
                          component={TextField}
                          variant={"outlined"}
                          size={"small"}
                          fullWidth
                          label="Guide Phone No"
                          name="guide_phone"
                          InputLabelProps={{
                            style: { fontSize: 14, color: "#929191" },
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item sm={6}>
                        <Field
                          component={TextField}
                          variant={"outlined"}
                          size={"small"}
                          fullWidth
                          label="Name"
                          name="guide_name"
                          InputLabelProps={{
                            style: { fontSize: 14, color: "#929191" },
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item sm={6}>
                        <Field
                          component={TextField}
                          variant={"outlined"}
                          size={"small"}
                          fullWidth
                          label="Contact"
                          name="guide_phone"
                          InputLabelProps={{
                            style: { fontSize: 14, color: "#929191" },
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  {withGuide === "yes" ? (
                    <Grid item sm={6}>
                      <Field
                        component={TextField}
                        variant={"outlined"}
                        size={"small"}
                        fullWidth
                        label="Guide Agent Name"
                        name="guide_agent_name"
                        InputLabelProps={{
                          style: { fontSize: 14, color: "#929191" },
                        }}
                      />
                    </Grid>
                  ) : (
                    <Grid item sm={6}>
                      <Field
                        component={TextField}
                        variant={"outlined"}
                        size={"small"}
                        fullWidth
                        label="Place"
                        name="guide_agent_name"
                        InputLabelProps={{
                          style: { fontSize: 14, color: "#929191" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleAction} variant={"outlined"} color={"error"} className={"text-capitalize"}>
                  cancel
                </Button>
                <Button variant={"contained"} type={"submit"} className={"text-capitalize"}>
                  {visitorId ? "Update" : "Confirm"}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
      {show && <SuccessPopup show={show} handleClose={handleClose} ticketId={ticketId} />}
    </Dialog>
  );
}
